import React from 'react';
import { Box, Typography } from '@mui/material';
import T from '../theme';
import { StatsBar, DataTable, WhyThisMatters } from '../components/Shared.jsx';

export function Op3Downloads({ data }) {
  if (data.op3Known === false) {
    return (
      <Box sx={{ p: 3, background: 'rgba(234,179,8,0.1)', border: `1px solid rgba(234,179,8,0.3)`, borderRadius: 1 }}>
        <Typography sx={{ fontWeight: 600, color: T.text }}>No OP3 Data Available</Typography>
        <Typography sx={{ fontSize: 13, color: T.textSecondary, mt: 0.5 }}>This show does not use the OP3 prefix in its RSS feed.</Typography>
        <Typography sx={{ fontSize: 12, color: T.textSecondary, mt: 0.75 }}>
          To enable OP3 analytics, prefix your episode audio URLs with <code>https://op3.dev/e/</code>
        </Typography>
      </Box>
    );
  }

  // Backend shape: { ok, op3Known, statsPageUrl, result: { show, totals, episode_table } }
  const result        = data.result || {};
  const { show = {}, totals = {}, episode_table = [] } = result;
  const statsUrl      = data.statsPageUrl || show.statsPageUrl;
  const sortedEps     = [...episode_table].sort((a, b) => (b.downloads_30d || 0) - (a.downloads_30d || 0));
  const top10         = sortedEps.slice(0, 10);
  const epsWithData   = episode_table.filter((e) => e.downloads_30d != null);
  const avg30Day      = epsWithData.length
    ? Math.round(epsWithData.reduce((s, e) => s + (e.downloads_30d || 0), 0) / epsWithData.length)
    : null;

  return (
    <Box>
      <StatsBar stats={{
        'Total 30-Day':       totals.approx_total_downloads_window != null ? Number(totals.approx_total_downloads_window).toLocaleString() : '—',
        'Avg / Episode (30d)': avg30Day != null ? avg30Day.toLocaleString() : '—',
        'Window':             `${totals.windowDays || 30} days`,
        'Episodes Tracked':   episode_table.length > 0 ? episode_table.length : '—',
      }} />

      {statsUrl && (
        <Box sx={{ mb: 2.5 }}>
          <Typography
            component="a"
            href={statsUrl}
            target="_blank"
            rel="noreferrer"
            sx={{ fontSize: 13, color: T.primary, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            View Full OP3 Stats Page →
          </Typography>
        </Box>
      )}

      {top10.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Top Episodes by Downloads</Typography>
          <DataTable
            columns={['Episode', '1-Day', '7-Day', '30-Day', 'All-Time', 'Published']}
            rows={top10.map((ep) => [
              ep.title || '—',
              ep.downloads_1d?.toLocaleString() ?? '—',
              ep.downloads_7d?.toLocaleString() ?? '—',
              ep.downloads_30d?.toLocaleString() ?? '—',
              ep.downloads_all_time?.toLocaleString() ?? '—',
              ep.pubdate
                ? new Date(ep.pubdate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '—',
            ])}
          />
        </Box>
      )}

      {episode_table.length === 0 && (
        <Box sx={{ p: 2, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 1, mb: 3 }}>
          <Typography sx={{ fontSize: 13, color: T.textSecondary }}>No episode download data returned from OP3 yet.</Typography>
        </Box>
      )}

      <WhyThisMatters text="Download trends indicate content resonance, audience growth, and episode performance." citations={data.expertCitations} />
    </Box>
  );
}
